/* GENTCREATE drawer motion controller (1.0.1, 2 Oct 2026) for the sidebar menu
 * (#sidebar-menu) and the cart drawer (#sidebar-cart). The slide itself is CSS
 * (gc-drawer-motion.css); this script only keeps everything around it in step.
 *
 * It does not open or close anything on its own account. theme.js, custom.js
 * and gc-cart-drawer.js all open and close these drawers by writing
 * aria-hidden on them, sometimes two of them in the same click, so this
 * watches that attribute and reacts once per real change:
 *
 *  - the scrim's curve: <html> gets gc-dr-open while a drawer is open and
 *    gc-dr-closing while it leaves, so .PageOverlay fades on the drawer's own
 *    duration and curve in both directions;
 *  - the scroll lock: gc-dr-lock from the moment a drawer opens until its
 *    close has FINISHED (theme.js drops its own no-scroll at the start of the
 *    close, and the scrollbar coming back mid-slide moved the panel by its
 *    width); gc-dr-sb and --gc-dr-sbw when the browser draws classic
 *    scrollbars, so the page keeps its width and nothing behind the scrim
 *    shifts;
 *  - will-change only while a panel moves (gc-dr-anim);
 *  - focus: into the panel on open (if the theme's focus() did not land),
 *    Tab and Shift+Tab wrap inside it, back to the button that opened it on
 *    close, by the X, Escape, or a tap or click on the scrim (1.0.1: a
 *    finger's tap on the scrim used to leave it on <body>, see armScrim);
 *    aria-expanded on the header buttons follows the drawer;
 *  - Escape and the scrim close the topmost drawer even when the drawer was
 *    opened by a path that did not arm theme.js's own listeners; the scrim
 *    closes on pointerdown (see armScrim);
 *  - the cart after "Add to Cart": product:added opens the drawer at once
 *    (custom.js, 300ms) while theme.js is still fetching the new cart, so it
 *    slid in empty and the line appeared half a second later. When the cart
 *    opens within 2.5s of a product:added and its content has not been
 *    replaced since, the panel waits off-screen (gc-dr-wait) until the new
 *    content lands and has been quiet for 110ms (at most 900ms), then slides
 *    in with it at the next idle moment (at most 120ms more). The scrim does not wait: the page steps back at once, so the
 *    tap is answered, and the cart arrives with the line already in it.
 */
(function () {
  'use strict';
  if (window.GCDrawerMotion) return;

  var doc = document;
  var root = doc.documentElement;
  var IDS = ['sidebar-menu', 'sidebar-cart'];
  var CART = 'sidebar-cart';
  var WAIT_WINDOW = 2500;  /* ms after product:added in which an opening cart waits */
  var WAIT_QUIET = 110;    /* ms of no further content swaps before it slides in */
  var WAIT_MAX = 900;      /* ms it waits at most */

  var state = {};          /* id -> { el, open, opener, waiting, timers... } */
  var stack = [];          /* open drawers, the most recent last */
  var lastOpener = {};     /* id -> { el, t } from the click that opened it */
  var closingTimer = 0;
  var addedAt = -1e9;
  var swappedAt = -1e9;
  var sbw = null;

  function now() { return window.performance && performance.now ? performance.now() : Date.now(); }

  /* the durations come from the stylesheet so CSS and JS cannot disagree */
  function cssMs(name, fallback) {
    var v = '';
    try { v = getComputedStyle(root).getPropertyValue(name).trim(); } catch (e) {}
    var n = parseFloat(v);
    if (isNaN(n)) return fallback;
    return /ms$/.test(v) ? n : /s$/.test(v) ? n * 1000 : n;
  }
  /* read once (and again when the stylesheet has loaded or the motion
     preference changes), never in the click that opens or closes a drawer:
     reading a custom property there forces a style recalculation in the
     very task that should hand the frame to the compositor */
  var dur = { open: 440, close: 340 };
  function readDurations() {
    dur.open = cssMs('--gc-dr-in', 440);
    dur.close = cssMs('--gc-dr-out', 340);
  }
  function inMs() { return dur.open; }
  function outMs() { return dur.close; }

  function scrollbarWidth() {
    if (sbw !== null) return sbw;
    var d = doc.createElement('div');
    d.style.cssText = 'position:absolute;top:-9999px;left:0;width:100px;height:100px;overflow:scroll;visibility:hidden';
    doc.body.appendChild(d);
    sbw = d.offsetWidth - d.clientWidth;
    doc.body.removeChild(d);
    return sbw;
  }

  function triggers(id) {
    return doc.querySelectorAll('[data-action="open-drawer"][data-drawer-id="' + id + '"]');
  }
  function shown(el) {
    return !!(el && el.getClientRects().length && getComputedStyle(el).visibility !== 'hidden');
  }
  function firstShown(list) {
    for (var i = 0; i < list.length; i++) if (shown(list[i])) return list[i];
    return null;
  }
  function setExpanded(id, open) {
    var t = triggers(id);
    for (var i = 0; i < t.length; i++) t[i].setAttribute('aria-expanded', open ? 'true' : 'false');
  }
  function focusQuietly(el) {
    try { el.focus({ preventScroll: true }); } catch (e) { try { el.focus(); } catch (e2) {} }
  }

  /* ------------------------------------------------------------ scroll lock */
  function lock() {
    if (root.classList.contains('gc-dr-lock')) return;
    var w = scrollbarWidth();
    if (w > 0) {
      root.style.setProperty('--gc-dr-sbw', w + 'px');
      root.classList.add('gc-dr-sb');
    }
    root.classList.add('gc-dr-lock');
  }
  function unlock() {
    root.classList.remove('gc-dr-lock');
    root.classList.remove('gc-dr-sb');
    root.style.removeProperty('--gc-dr-sbw');
  }

  /* --------------------------------------------------------- moving flag */
  function anyMoving() {
    for (var k in state) if (state.hasOwnProperty(k) && state[k].moving) return true;
    return false;
  }
  function moving(s, ms) {
    s.moving = true;
    s.el.classList.add('gc-dr-anim');
    root.classList.add('gc-dr-anim');
    clearTimeout(s.moveTimer);
    s.moveTimer = setTimeout(function () { settled(s); }, ms + 80);
  }
  function settled(s) {
    clearTimeout(s.moveTimer);
    s.moving = false;
    s.el.classList.remove('gc-dr-anim');
    if (!anyMoving()) root.classList.remove('gc-dr-anim');
  }

  /* ------------------------------------------------- cart: wait for content */
  function maybeWait(s) {
    if (s.id !== CART) return;
    if (!(now() - addedAt < WAIT_WINDOW && swappedAt <= addedAt)) return;
    s.waiting = true;
    s.el.classList.add('gc-dr-wait');
    s.waitMax = setTimeout(function () { release(s); }, WAIT_MAX);
  }
  function contentSwapped(s) {
    if (!s.waiting) return;
    clearTimeout(s.waitQuiet);
    s.waitQuiet = setTimeout(function () { release(s); }, WAIT_QUIET);
  }
  function release(s) {
    if (!s.waiting) return;
    s.waiting = false;
    clearTimeout(s.waitMax);
    clearTimeout(s.waitQuiet);
    /* After an add the main thread is busy with the cart re-renders and the
       analytics tags reacting to it. The slide is a compositor animation once
       its first frame is committed, so that frame is taken at an idle moment
       (at most 120ms later), then the class is dropped on the next frame. */
    var go = function () {
      requestAnimationFrame(function () {
        s.el.classList.remove('gc-dr-wait');
        if (s.open) moving(s, inMs());
      });
    };
    if (window.requestIdleCallback) requestIdleCallback(go, { timeout: 120 });
    else setTimeout(go, 0);
  }
  function cancelWait(s) {
    if (!s.waiting) return;
    s.waiting = false;
    clearTimeout(s.waitMax);
    clearTimeout(s.waitQuiet);
    s.el.classList.remove('gc-dr-wait');
  }

  /* ------------------------------------------------------------ open/close */
  function onOpen(s) {
    if (s.open) return;
    s.open = true;
    stack = stack.filter(function (x) { return x !== s.id; });
    stack.push(s.id);
    clearTimeout(closingTimer);
    root.classList.remove('gc-dr-closing');
    root.classList.add('gc-dr-open');
    lock();
    maybeWait(s);
    if (!s.waiting) moving(s, inMs());
    setExpanded(s.id, true);
    var o = lastOpener[s.id];
    s.opener = o && now() - o.t < 2000 ? o.el : null;
    /* theme.js focuses the panel as it opens; if that did not land (a path
       that only wrote aria-hidden), put the focus in the panel so Tab and
       Escape work from the first key press */
    requestAnimationFrame(function () {
      if (!s.open || s.el.contains(doc.activeElement)) return;
      if (!s.el.hasAttribute('tabindex')) s.el.setAttribute('tabindex', '-1');
      focusQuietly(s.el);
    });
  }

  function onClose(s) {
    if (!s.open) return;
    s.open = false;
    cancelWait(s);
    stack = stack.filter(function (x) { return x !== s.id; });
    setExpanded(s.id, false);
    var ms = outMs();
    moving(s, ms);
    if (!stack.length) {
      root.classList.remove('gc-dr-open');
      root.classList.add('gc-dr-closing');
      clearTimeout(closingTimer);
      closingTimer = setTimeout(function () {
        if (stack.length) return;
        root.classList.remove('gc-dr-closing');
        unlock();
      }, ms + 30);
    }
    /* back to the button that opened it, on the next frame, so the
       visibility check does not force a style pass inside the close */
    requestAnimationFrame(function () { restoreFocus(s); });
  }

  /* Focus goes back to the button that opened the drawer, unless it has
     already gone somewhere else on purpose (another overlay, a link): only
     when it is still in the closed panel or has fallen back to <body>. */
  function restoreFocus(s) {
    if (s.open) return;
    var a = doc.activeElement;
    if (!a || a === doc.body || a === root || s.el.contains(a)) {
      var t = s.opener && shown(s.opener) ? s.opener : firstShown(triggers(s.id));
      if (t) focusQuietly(t);
    }
  }

  function closeVia(s) {
    var b = s.el.querySelector('[data-action="close-drawer"]');
    if (b) b.click();
    else s.el.setAttribute('aria-hidden', 'true');
  }

  /* --------------------------------------------------------------- wiring */
  function watch(id) {
    var el = doc.getElementById(id);
    if (!el) return;
    var s = state[id];
    if (s && s.el === el) return;
    if (s && s.obs) s.obs.disconnect();
    s = state[id] = { id: id, el: el, open: false, moving: false, waiting: false };
    s.obs = new MutationObserver(function (list) {
      for (var i = 0; i < list.length; i++) {
        var m = list[i];
        if (m.type === 'attributes' && m.target === el) {
          if (el.getAttribute('aria-hidden') === 'false') onOpen(s); else onClose(s);
        } else if (m.type === 'childList' && id === CART &&
                   (m.target === el || (m.target.matches && m.target.matches('.Cart, .Drawer__Main, .Drawer__Footer')))) {
          swappedAt = now();
          contentSwapped(s);
        }
      }
    });
    s.obs.observe(el, { attributes: true, attributeFilter: ['aria-hidden'], childList: id === CART, subtree: id === CART });
    el.addEventListener('transitionend', function (e) {
      if (e.target === el && e.propertyName === 'transform' && !s.waiting) settled(s);
    });
    if (el.getAttribute('aria-hidden') === 'false') onOpen(s);
  }
  function init() {
    for (var i = 0; i < IDS.length; i++) watch(IDS[i]);
    if (sbw === null && doc.body) scrollbarWidth();
  }

  /* which button opened which drawer, for the focus on the way back */
  doc.addEventListener('click', function (e) {
    var t = e.target && e.target.closest && e.target.closest('[data-action="open-drawer"][data-drawer-id]');
    if (t) lastOpener[t.getAttribute('data-drawer-id')] = { el: t, t: now() };
  }, true);

  /* a product was added: the cart that opens next waits for its new content */
  window.addEventListener('product:added', function () { addedAt = now(); }, true);

  /* Tab and Shift+Tab wrap inside the open drawer. theme.js only pulls the
     focus back when it lands on something outside, so it could still leave
     the page for the browser's own controls (or the preview bar's iframe) */
  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  function focusables(el) {
    var all = el.querySelectorAll(FOCUSABLE), out = [];
    for (var i = 0; i < all.length; i++) {
      var n = all[i];
      if (n.getClientRects().length && getComputedStyle(n).visibility === 'visible') out.push(n);
    }
    return out;
  }
  doc.addEventListener('keydown', function (e) {
    if (e.key !== 'Tab' || e.defaultPrevented || !stack.length) return;
    var s = state[stack[stack.length - 1]];
    if (!s || !s.open) return;
    var list = focusables(s.el);
    if (!list.length) { e.preventDefault(); focusQuietly(s.el); return; }
    var a = doc.activeElement, first = list[0], last = list[list.length - 1];
    var inside = s.el.contains(a);
    if (e.shiftKey && (a === first || a === s.el || !inside)) { e.preventDefault(); focusQuietly(last); }
    else if (!e.shiftKey && (a === last || !inside)) { e.preventDefault(); focusQuietly(first); }
  });

  doc.addEventListener('keydown', function (e) {
    if ((e.key !== 'Escape' && e.key !== 'Esc') || e.defaultPrevented || !stack.length) return;
    var s = state[stack[stack.length - 1]];
    if (!s) return;
    var a = doc.activeElement;
    /* an overlay of its own above the drawer (and holding the focus) handles its own Escape */
    if (a && a !== doc.body && !s.el.contains(a) && a.closest &&
        a.closest('[role="dialog"], [aria-modal="true"], .Modal, .Drawer, .Popover.is-visible')) return;
    closeVia(s);
  });

  /* The scrim closes on pointerdown, not on the click that follows. A click
     anywhere on the page runs the TikTok pixel's click auto-tracking, and on
     this large, plain element it took ~200ms (measured, headless 1440) inside
     the same task as the close, so the slide-out started late. Closing on
     pointerdown commits the slide before that click is dispatched; once
     committed, a transform transition runs on the compositor and a busy
     main thread no longer holds it. The click itself still reaches the scrim
     and finds nothing left to close. */
  function scrimTop(ov) {
    if (!stack.length || !ov.classList.contains('is-visible')) return null;
    return state[stack[stack.length - 1]] || null;
  }
  function armScrim() {
    var ov = doc.querySelector('.PageOverlay');
    if (!ov || ov.__gcDr) return;
    ov.__gcDr = 1;
    ov.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      var s = scrimTop(ov);
      if (!s || !s.open) return;
      closeVia(s);
      /* the scrim keeps catching the pointer until this press has produced
         its click, so a long press cannot click through to the page once the
         scrim has faded (armed after closeVia, whose own synthetic click on
         the close button must not count as that click) */
      ov.classList.add('gc-dr-catch');
      var t = 0, done = false;
      var free = function () {
        if (done) return;
        done = true;
        clearTimeout(t);
        doc.removeEventListener('click', free, true);
        setTimeout(function () { ov.classList.remove('gc-dr-catch'); }, 0);
        /* the press is over: if anything in it still dropped the focus to
           <body>, it goes back to the opener */
        requestAnimationFrame(function () { restoreFocus(s); });
      };
      doc.addEventListener('click', free, true);
      doc.addEventListener('pointerup', function up() {
        doc.removeEventListener('pointerup', up, true);
        t = setTimeout(free, 700);
      }, true);
      doc.addEventListener('pointercancel', function cancel() {
        doc.removeEventListener('pointercancel', cancel, true);
        free();
      }, true);
    });
    /* The scrim never takes the focus. A finger's tap gets its compatibility
       mousedown only after the finger lifts, by which time the close above
       has already put the focus back on the opener; the scrim is not
       focusable, so the default action of that mousedown blurred the opener
       and left the focus on <body>. While the scrim belongs to one of these
       drawers (open, or still catching the press that closed it) its
       mousedown keeps the focus where it is. With a mouse the same mousedown
       comes right after pointerdown, so the focus stays in the panel until
       the close moves it to the opener, as before. The click still fires. */
    ov.addEventListener('mousedown', function (e) {
      if (ov.classList.contains('gc-dr-catch') || scrimTop(ov)) e.preventDefault();
    });
    /* theme.js closes on the click when it opened the drawer itself; this is
       the fallback for the paths that did not arm its listener, and for
       browsers without pointer events */
    ov.addEventListener('click', function () {
      var s = scrimTop(ov) || (stack.length ? state[stack[stack.length - 1]] : null);
      setTimeout(function () { if (s && s.open) closeVia(s); }, 0);
    });
  }

  function start() { init(); armScrim(); readDurations(); }
  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', start);
  else start();
  /* the stylesheet is loaded without blocking the page; read again once it is in */
  window.addEventListener('load', function () { setTimeout(readDurations, 0); });
  try {
    var mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    var onMq = function () { setTimeout(readDurations, 0); };
    if (mq.addEventListener) mq.addEventListener('change', onMq); else if (mq.addListener) mq.addListener(onMq);
  } catch (e) {}
  /* the theme editor re-renders sections in place */
  doc.addEventListener('shopify:section:load', function () { setTimeout(start, 0); });

  window.GCDrawerMotion = { version: '1.0.1', state: state, dur: dur };
})();
